import React, { useEffect } from 'react'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import '@/styles/components/confirm-modal.css'

interface ConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  /**
   * Optional. Where the message is already a full question, a title just says
   * the same thing twice, so the header collapses to the close button.
   */
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  isDestructive?: boolean
  isLoading?: boolean
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
  isLoading = false
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose()
      }
    }

    let releaseScroll: (() => void) | undefined
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      releaseScroll = lockBodyScroll()
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      releaseScroll?.()
    }
  }, [isOpen, isLoading, onClose])

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isLoading) {
      onClose()
    }
  }

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm()
    }
  }

  const handleCancel = () => {
    if (!isLoading) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-container confirm-modal" role="dialog" aria-modal="true">
        {/* No title means the message is the whole question, so there is no
            header at all. The close button goes with it: Cancel already
            dismisses, and two ways out of a two-button question is one too
            many. */}
        {title && (
          <div className="modal-header">
            <h2>{title}</h2>
            {!isLoading && (
              <button className="modal-close-floating" onClick={onClose} aria-label="Close modal">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {!title && !isLoading && (
          <button
            type="button"
            className="modal-close-floating"
            onClick={onClose}
            aria-label="Close"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <div className={`modal-content${title ? '' : ' is-headless'}`}>
          <p className="confirm-message">{message}</p>
        </div>

        <div className="modal-footer">
          <button
            className="btn-cancel"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            className={`btn-confirm ${isDestructive ? 'btn-destructive' : ''}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="btn-spinner" />
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConfirmModal
